import { crush, get, isNumber, pick, uid, unique } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import { PortHelpers } from '@diagram/helpers/PortHelpers';

import {
    CurrentOSZones,
    CurrentOSZoneType,
    Diagram,
    DiagramComponentInstance,
    DiagramConnection,
    getFlowDirection,
    getPortPowerFlowDirection,
    isProtectionDevice,
    isZoneEndpoint,
    MeasurementSystem,
    powerConverter,
    PowerFlowDiagram,
    Voltage,
    voltageConverter,
    Zone,
    ZoneValidationError,
    ZoneValidationErrorType,
} from 'models';
import { CryptoHelpers } from 'helpers/CryptoHelpers';
import { FormatHelpers } from 'helpers/formatters';

import { getMinAndMaxVoltageForZone } from '../validators/zones/zone-voltage-levels';

type DataPoint = {
    voltage: { value: number; unit: string };
    power: { value: number; unit: string };
};

const CURRENT_OS_ZONE_0_SOURCE_TYPES: DiagramComponentInstance['componentType'][] = [
    'battery',
    'capacitor',
    'fuelCell',
    'generator',
    'hydro',
    'wind',
];

const CURRENT_OS_SOURCE_TYPES: DiagramComponentInstance['componentType'][] = [
    ...CURRENT_OS_ZONE_0_SOURCE_TYPES,
    'solar',
    'utility',
];

const VOLTAGE_SOURCE_TYPES: DiagramComponentInstance['componentType'][] = ['battery', 'utility'];

const newZone = (type: Zone['type'], id?: Zone['id']) =>
    ({
        id: id ? id : `zone-${uid(6)}`,
        type,
        connections: [] as DiagramConnection[],
        componentInstances: [] as DiagramComponentInstance[],
    }) as Zone;

const ZoneHelpers = {
    generate: (
        diagram: PowerFlowDiagram,
        type: Zone['type'],
        isZoneEndpoint: isZoneEndpoint,
        previousZones: Zone[],
    ): Zone[] => {
        const zones: Zone[] = [];

        const connections = Object.values(diagram.connections).filter((connection) => {
            return [connection.from, connection.to].every((endpoint) => {
                const componentInstance = diagram.componentInstances[endpoint.componentInstanceId];

                return !!componentInstance && !componentInstance.disabled;
            });
        });

        const connectionsToProcess = new Set<DiagramConnection>(connections);
        const visitedConnections = new Set<DiagramConnection['id']>();

        const traverse = (connection: DiagramConnection, zone: Zone) => {
            if (visitedConnections.has(connection.id)) {
                return zone;
            }

            visitedConnections.add(connection.id);
            connectionsToProcess.delete(connection);

            zone.connections.push(connection);

            for (const endpoint of [connection.from, connection.to]) {
                const componentInstance = diagram.componentInstances[endpoint.componentInstanceId];

                if (!componentInstance) {
                    // Sometimes a component instance is not found, skip it.
                    continue;
                }

                zone.componentInstances.push(componentInstance);

                if (!isZoneEndpoint(componentInstance, endpoint.port)) {
                    const otherConnections = connections.filter(
                        (c) =>
                            c.from.componentInstanceId === componentInstance.id ||
                            c.to.componentInstanceId === componentInstance.id,
                    );

                    otherConnections.forEach((otherConnection) => {
                        let skip = false;

                        if (componentInstance.id === otherConnection.from.componentInstanceId) {
                            skip = isZoneEndpoint(componentInstance, otherConnection.from.port);
                        } else if (componentInstance.id === otherConnection.to.componentInstanceId) {
                            skip = isZoneEndpoint(componentInstance, otherConnection.to.port);
                        }

                        if (skip) return;

                        traverse(otherConnection, zone);
                    });
                }
            }

            return zone;
        };

        while (connectionsToProcess.size) {
            const [connectionToProcess] = connectionsToProcess;

            // Recycle the id of the previous zone if the connection is part of a previous zone
            const id = previousZones.find((zone) => {
                return zone.connections.some((connection) => connection.id === connectionToProcess.id);
            })?.id;

            const zone = traverse(connectionToProcess, newZone(type, id));

            zone.componentInstances = unique(zone.componentInstances, (componentInstance) => componentInstance.id);

            zones.push(zone);
        }

        return zones;
    },

    generateControlZones: (diagram: Diagram, previousZones: Zone[]) => {
        return ZoneHelpers.generate(diagram, 'control', ZoneHelpers.isControlZoneEndpoint, previousZones);
    },

    isControlZoneEndpoint: ((componentInstance) => {
        return ComponentInstanceHelpers.actsAsConverter(componentInstance);
    }) as isZoneEndpoint,

    generateFaultZones: (diagram: Diagram, previousZones: Zone[]) => {
        const controlZones = ZoneHelpers.generateControlZones(diagram, previousZones);

        return controlZones.map((controlZone) => {
            const faultFeedingZones = ZoneHelpers.generateFaultFeedingZones(diagram, controlZones, controlZone);

            const allConnections = unique(
                [...controlZone.connections, ...faultFeedingZones.flatMap((feedingZone) => feedingZone.connections)],
                (conn) => conn.id,
            );

            const allComponentInstances = unique(
                [
                    ...controlZone.componentInstances,
                    ...faultFeedingZones.flatMap((feedingZone) => feedingZone.componentInstances),
                ],
                (ci) => ci.id,
            );

            const faultZone: Zone = {
                id: controlZone?.id ?? `zone-${uid(6)}`,
                type: 'fault',
                initConnections: controlZone.connections,
                connections: allConnections,
                componentInstances: allComponentInstances,
            };

            const generateComponentInstanceKeys = (numberOfPorts: number) => {
                const ports = Array.from({ length: numberOfPorts }, (_, i) => i.toString());

                return [
                    'componentType',
                    'multiple.type',
                    'multiple.amount',
                    'multiple.series',

                    ...ports.flatMap((port) =>
                        [
                            'capacitor.capacitance.nom',
                            'capacitor.resistance.nom',
                            'equivalentSeriesResistance.nom',
                            'reverseDiode',
                            'resistance.nom',
                            'inductance.nom',
                            'isolated',
                            'faultFeedingPorts',
                            'voltageType',
                            'AC.voltage.min',
                            'AC.voltage.nom',
                            'AC.voltage.max',
                            'AC.frequency',
                            'DC.voltage.min',
                            'DC.voltage.nom',
                            'DC.voltage.max',
                            'DC.faultSteadyStateCurrent.nom',
                            'DC.faultSteadyStateCurrent.min',
                            'DC.faultSteadyStateCurrent.max',
                        ].map((field) => `specifications.electrical.ports.${port}.${field}`),
                    ),

                    ...ports.flatMap((port) =>
                        ['voltageType', 'voltage.value', 'controlMethod'].map(
                            (field) => `configuration.ports.${port}.${field}`,
                        ),
                    ),
                ];
            };

            const CONNECTION_KEYS = [
                'from.componentInstanceId',
                'from.port',
                'to.componentInstanceId',
                'to.port',
                'resistance.nom',
                'inductance.nom',
                'lines.AC.L1',
                'lines.AC.L2',
                'lines.AC.L3',
                'lines.AC.N',
                'lines.AC.PE',
                'lines.DC.L+',
                'lines.DC.M',
                'lines.DC.L-',
                'lines.DC.PE',
                'voltage.max',
                'voltage.min',
                'voltage.nom',
                'requirements.voltage.value',
                'multiple.parallel',
            ];

            const extractComponentInstanceFields = (componentInstance: DiagramComponentInstance) => {
                const crushed = crush(componentInstance);
                const numberOfPorts = componentInstance.specifications.electrical.ports.length;
                return pick(crushed, generateComponentInstanceKeys(numberOfPorts) as (keyof typeof crushed)[]);
            };

            const extractConnectionFields = (connection: DiagramConnection) => {
                const crushed = crush(connection);
                return pick(crushed, CONNECTION_KEYS as (keyof typeof crushed)[]);
            };

            const zoneData = {
                componentInstances: faultZone.componentInstances.map(extractComponentInstanceFields),
                connections: faultZone.connections.map(extractConnectionFields),
            };

            return {
                ...faultZone,
                hash: CryptoHelpers.unsafeSimpleHash(JSON.stringify(zoneData)),
            };
        });
    },

    generateFaultFeedingZones: (diagram: Diagram, controlZones: Zone[], controlZone: Zone): Zone[] => {
        const zoneEndpoints = controlZone.componentInstances.filter((ci) =>
            ZoneHelpers.isControlZoneEndpoint(ci, null),
        );

        const adjacentZones: Zone[] = [];

        zoneEndpoints.forEach((endpoint) => {
            const endpointConnections = Object.values(diagram.connections).filter(
                (c) => c.from.componentInstanceId === endpoint.id || c.to.componentInstanceId === endpoint.id,
            );
            const endpointZoneConnections = endpointConnections.filter((connection) =>
                controlZone.connections.some((c) => c.id === connection.id),
            );
            const endpointNonZoneConnections = endpointConnections.filter(
                (connection) => !controlZone.connections.some((c) => c.id === connection.id),
            );

            const controlZonePortIndices = endpointZoneConnections
                .map((connection) => {
                    return connection.from.componentInstanceId === endpoint.id
                        ? connection.from.port
                        : connection.to.port;
                })
                .filter((portIndex): portIndex is number => isNumber(portIndex));

            const faultFeedingPortIndices = controlZonePortIndices.flatMap((portIndex) => {
                const portSpecs = endpoint.specifications.electrical?.ports[portIndex];

                return portSpecs && 'faultFeedingPorts' in portSpecs ? portSpecs.faultFeedingPorts : [];
            });

            if (!faultFeedingPortIndices.length) return;

            endpointNonZoneConnections.forEach((connection) => {
                const portIndex =
                    connection.from.componentInstanceId === endpoint.id ? connection.from.port : connection.to.port;

                if (!isNumber(portIndex) || !faultFeedingPortIndices.includes(portIndex)) return;

                controlZones.forEach((zone) => {
                    if (zone.connections.some((c) => c.id === connection.id) && !adjacentZones.includes(zone)) {
                        adjacentZones.push(zone);
                    }
                });
            });
        });

        return adjacentZones;
    },

    generateGalvanicZones: (diagram: Diagram, previousZones: Zone[]) => {
        return ZoneHelpers.generate(
            diagram,
            'galvanic',
            (componentInstance, port) => {
                if (port === null) {
                    return false;
                }

                const portSpecifications = componentInstance.specifications.electrical.ports[port];

                if (portSpecifications && 'isolated' in portSpecifications) {
                    return !!portSpecifications.isolated;
                }

                if (componentInstance.componentType === 'transformer') {
                    return true;
                }

                return false;
            },
            previousZones,
        );
    },

    // An island is everything connected to everything else, so nothing splits it. `generate` needs a zone
    // type, but it is meaningless for an island, so it is dropped.
    islands: (diagram: PowerFlowDiagram) => {
        return ZoneHelpers.generate(diagram, 'control', () => false, []).map((zone) =>
            pick(zone, ['componentInstances', 'connections']),
        );
    },

    generateProtectionZones: (diagram: Diagram, previousZones: Zone[]) => {
        const isProtectionZoneEndpoint: isZoneEndpoint = (componentInstance) => {
            return isProtectionDevice(componentInstance.componentType);
        };

        return ZoneHelpers.generate(diagram, 'protection', isProtectionZoneEndpoint, previousZones);
    },

    generateCurrentOSZones: (diagram: Diagram, previousZones: CurrentOSZones): CurrentOSZones => {
        const isCurrentOSZoneEndpoint: isZoneEndpoint = (componentInstance) => {
            const definition = ComponentInstanceHelpers.definition(componentInstance.componentType);

            const { type, actsAsConverter } = definition;

            return type === 'breaker' || type === 'fuse' || !!actsAsConverter;
        };

        const flattenedPreviousZones = Object.values(previousZones).flat();

        const zones = ZoneHelpers.generate(diagram, 'currentOS', isCurrentOSZoneEndpoint, flattenedPreviousZones);

        const currentOSZones: CurrentOSZones = {
            0: [],
            1: [],
            2.1: [],
            2.2: [],
            3: [],
            4: [],
        };

        const ACzones: Zone[] = [];

        zones.forEach((zone) => {
            const connection = zone.connections[0];
            const fromCI = connection ? diagram.componentInstances[connection.from.componentInstanceId] : undefined;
            const toCI = connection ? diagram.componentInstances[connection.to.componentInstanceId] : undefined;
            const voltageType =
                connection &&
                fromCI &&
                toCI &&
                isNumber(connection.from.port) &&
                isNumber(connection.to.port) &&
                fromCI.configuration.ports?.[connection.from.port]?.voltageType ===
                    toCI.configuration.ports?.[connection.to.port]?.voltageType
                    ? fromCI.configuration.ports?.[connection.from.port]?.voltageType
                    : null;

            if (voltageType === 'AC') {
                ACzones.push(zone);
            }
        });

        const assignZoneType = (zone: Zone, zoneType: CurrentOSZoneType) => {
            if (zone.type === 'currentOS') {
                zone.currentOSZoneType = zoneType;
            }

            currentOSZones[zoneType].push(zone);
        };

        const unclassifiedZones = () =>
            zones.filter(
                (zone) =>
                    !ACzones.includes(zone) &&
                    !Object.values(currentOSZones).some((classified) => classified.includes(zone)),
            );

        const getZoneEndpointPorts = (zone: Zone) =>
            zone.connections
                .flatMap(({ from, to }) => [from, to])
                .map(({ componentInstanceId, port }) => ({
                    componentInstance: diagram.componentInstances[componentInstanceId],
                    port,
                }))
                .filter(
                    (endpoint): endpoint is { componentInstance: DiagramComponentInstance; port: number } =>
                        !!endpoint.componentInstance &&
                        !endpoint.componentInstance.disabled &&
                        isNumber(endpoint.port) &&
                        isCurrentOSZoneEndpoint(endpoint.componentInstance, endpoint.port),
                );

        unclassifiedZones().forEach((zone) => {
            const isZone0 = zone.componentInstances.some((componentInstance) => {
                return CURRENT_OS_ZONE_0_SOURCE_TYPES.includes(componentInstance.componentType);
            });

            if (isZone0) {
                assignZoneType(zone, 0);
            }
        });

        const isConventionalZone1Device = (componentInstance: DiagramComponentInstance) => {
            if (!['breaker', 'fuse', 'contactor', 'disconnect'].includes(componentInstance.componentType)) {
                return false;
            }

            if (componentInstance.componentType === 'breaker') {
                // @ts-ignore
                return componentInstance.specifications.electrical?.openingTime === 'conventional';
            }

            return true;
        };

        let zone1Spread = true;

        while (zone1Spread) {
            zone1Spread = false;

            unclassifiedZones().forEach((zone) => {
                const sharesConventionalDevice = zone.componentInstances.some((a) =>
                    [...currentOSZones[0], ...currentOSZones[1]].some((classified) =>
                        classified.componentInstances.some((b) => a.id === b.id && isConventionalZone1Device(a)),
                    ),
                );

                if (sharesConventionalDevice) {
                    assignZoneType(zone, 1);
                    zone1Spread = true;
                }
            });
        }

        const isUltraFastUnidirectionalFlowDevice = (componentInstance: DiagramComponentInstance) =>
            // @ts-ignore
            componentInstance.specifications.electrical?.openingTime === 'ultra-fast' &&
            getFlowDirection(componentInstance) === 'unidirectional';

        const isZone4FedConverter = (zone: Zone, componentInstance: DiagramComponentInstance) =>
            ComponentInstanceHelpers.actsAsConverter(componentInstance) &&
            getFlowDirection(componentInstance) === 'unidirectional' &&
            currentOSZones[4].some(
                (upstream) =>
                    upstream !== zone && upstream.componentInstances.some(({ id }) => id === componentInstance.id),
            );

        const canFeed = (componentInstance: DiagramComponentInstance) =>
            CURRENT_OS_SOURCE_TYPES.includes(componentInstance.componentType) ||
            (ComponentInstanceHelpers.actsAsConverter(componentInstance) &&
                getFlowDirection(componentInstance) !== 'unidirectional');

        const hasSourcesDownstream = (zone: Zone, feeder: DiagramComponentInstance) =>
            ZoneHelpers.generate(diagram, 'currentOS', ({ id }) => id === feeder.id, [])
                .find((downstream) => downstream.connections.some(({ id }) => id === zone.connections[0].id))
                ?.componentInstances.some(canFeed);

        const qualifiesForZone4 = (zone: Zone) => {
            const hasFastProtectionDevices = zone.componentInstances.some(
                // @ts-ignore
                ({ specifications }) => specifications.electrical?.openingTime === 'fast',
            );

            const hasConventionalProtectionDevices = zone.componentInstances.some(
                // @ts-ignore
                ({ specifications }) => specifications.electrical?.openingTime === 'conventional',
            );

            const zoneEndpointPorts = getZoneEndpointPorts(zone).filter(
                ({ componentInstance }) =>
                    ComponentInstanceHelpers.definition(componentInstance.componentType).type !== 'fuse',
            );

            const hasBidirectionalZoneEndpointPorts = zoneEndpointPorts.some(
                ({ componentInstance, port }) => getPortPowerFlowDirection(componentInstance, port) === 'bidirectional',
            );

            const feedingZoneEndpoints = unique(
                zoneEndpointPorts.filter(
                    ({ componentInstance, port }) => getPortPowerFlowDirection(componentInstance, port) === 'output',
                ),
                ({ componentInstance }) => componentInstance.id,
            );

            const isFedByOneUnidirectionalDevice =
                feedingZoneEndpoints.length === 1 &&
                (isUltraFastUnidirectionalFlowDevice(feedingZoneEndpoints[0].componentInstance) ||
                    isZone4FedConverter(zone, feedingZoneEndpoints[0].componentInstance));

            return (
                isFedByOneUnidirectionalDevice &&
                !hasFastProtectionDevices &&
                !hasConventionalProtectionDevices &&
                !hasBidirectionalZoneEndpointPorts &&
                !hasSourcesDownstream(zone, feedingZoneEndpoints[0].componentInstance)
            );
        };

        let zone4Spread = true;

        while (zone4Spread) {
            zone4Spread = false;

            unclassifiedZones().forEach((zone) => {
                if (qualifiesForZone4(zone)) {
                    assignZoneType(zone, 4);
                    zone4Spread = true;
                }
            });
        }

        unclassifiedZones().forEach((zone) => {
            const hasProtectiveDevices = zone.componentInstances.some(
                ({ componentType }) => componentType === 'breaker',
            );

            const hasFastProtectionDevices = zone.componentInstances.some(
                // @ts-ignore
                ({ specifications }) => specifications.electrical?.openingTime === 'fast',
            );

            const hasUltraFastProtectionDevices = zone.componentInstances.some(
                // @ts-ignore
                ({ specifications }) => specifications.electrical?.openingTime === 'ultra-fast',
            );

            const hasConventionalProtectionDevices = zone.componentInstances.some(
                // @ts-ignore
                ({ specifications }) => specifications.electrical?.openingTime === 'conventional',
            );

            const hasOnlyUltraFastProtectionDevices =
                hasProtectiveDevices &&
                hasUltraFastProtectionDevices &&
                !hasFastProtectionDevices &&
                !hasConventionalProtectionDevices;

            const hasConverterFedZoneEndpointPorts = getZoneEndpointPorts(zone).some(
                ({ componentInstance, port }) =>
                    ComponentInstanceHelpers.actsAsConverter(componentInstance) &&
                    getPortPowerFlowDirection(componentInstance, port) !== 'input',
            );

            const isZone3 = hasOnlyUltraFastProtectionDevices && !hasConverterFedZoneEndpointPorts;

            if (isZone3) {
                assignZoneType(zone, 3);
                return;
            }

            const hasOnlyFastProtectionDevices =
                hasProtectiveDevices && hasFastProtectionDevices && !hasConventionalProtectionDevices;

            const isZone2_2 = hasOnlyFastProtectionDevices && !hasConverterFedZoneEndpointPorts;

            if (isZone2_2) {
                assignZoneType(zone, 2.2);
                return;
            }

            const isAdjacentToZone0 = zone.componentInstances.some((a) => {
                return currentOSZones[0].some((zone) =>
                    zone.componentInstances.some(
                        (b) =>
                            a.id === b.id && ['breaker', 'fuse', 'contactor', 'disconnect'].includes(a.componentType),
                    ),
                );
            });

            const isZone2_1 = !isAdjacentToZone0;

            if (isZone2_1) {
                assignZoneType(zone, 2.1);
                return;
            }

            // In all other cases, it is zone 1
            assignZoneType(zone, 1);
        });

        return currentOSZones;
    },

    error: (
        errors: ZoneValidationError[],
        type: ZoneValidationErrorType,
        zone: Zone,
        componentInstances: DiagramComponentInstance[] = [],
        connections: DiagramConnection[] = [],
    ) => {
        errors.push({
            id: uid(6),
            type,
            level: 'error',
            payload: {
                zone,
                componentInstances,
                connections,
            },
        });
    },

    warning: (
        errors: ZoneValidationError[],
        type: ZoneValidationErrorType,
        zone: Zone,
        componentInstances: DiagramComponentInstance[] = [],
        connections: DiagramConnection[] = [],
    ) => {
        errors.push({
            id: uid(6),
            type,
            level: 'warning',
            payload: {
                zone,
                componentInstances,
                connections,
            },
        });
    },

    getZone: (zones: Zone[], componentInstanceId: DiagramComponentInstance['id'], port: number): Zone | null => {
        return (
            zones.find((zone) => {
                let connections: Zone['connections'];

                if ('initConnections' in zone && zone.initConnections?.length) {
                    connections = zone.initConnections;
                } else {
                    connections = zone.connections;
                }

                return connections.some((connection) => {
                    const { from, to } = connection;

                    return (
                        (from.componentInstanceId === componentInstanceId && from.port === port) ||
                        (to.componentInstanceId === componentInstanceId && to.port === port)
                    );
                });
            }) ?? null
        );
    },

    getConnectedPorts: (zone: Zone, componentInstanceId: DiagramComponentInstance['id']): number[] => {
        const ports: number[] = [];

        zone.connections.forEach(({ from, to }) => {
            if (from.componentInstanceId === componentInstanceId && isNumber(from.port)) {
                ports.push(from.port);
            }

            if (to.componentInstanceId === componentInstanceId && isNumber(to.port)) {
                ports.push(to.port);
            }
        });

        return unique(ports);
    },

    getVoltageControllableComponentInstances: (zone: Zone): DiagramComponentInstance[] => {
        return zone.componentInstances.filter((componentInstance) => {
            if (VOLTAGE_SOURCE_TYPES.includes(componentInstance.componentType)) {
                return true;
            }

            return ZoneHelpers.getConnectedPorts(zone, componentInstance.id).some((port) =>
                PortHelpers.canControlVoltage(componentInstance, port),
            );
        });
    },

    isFixedVoltageSource: (zone: Zone, componentInstance: DiagramComponentInstance): boolean => {
        if (!VOLTAGE_SOURCE_TYPES.includes(componentInstance.componentType)) {
            return false;
        }

        return !ZoneHelpers.getConnectedPorts(zone, componentInstance.id).some((port) =>
            PortHelpers.getAvailableControlMethods(componentInstance, port).includes('power-voltage'),
        );
    },

    getZoneErrors: (zone: Zone, errors: ZoneValidationError[]): ZoneValidationError[] => {
        return errors.filter((error) => {
            return error.payload.zone.id === zone.id && error.level === 'error';
        });
    },

    getZoneWarnings: (zone: Zone, errors: ZoneValidationError[]): ZoneValidationError[] => {
        return errors.filter((error) => {
            return error.payload.zone.id === zone.id && error.level === 'warning';
        });
    },

    getDroopCurves: (zone: Zone): Record<DiagramComponentInstance['id'], DataPoint[]> => {
        const droopCurves: Record<DiagramComponentInstance['id'], DataPoint[]> = {};

        const { connections, componentInstances } = zone;

        const pushPointsToCurve = (endpoint: DiagramConnection['from'] | DiagramConnection['to']) => {
            const { componentInstanceId, port: portIndex } = endpoint;

            if (droopCurves[componentInstanceId]) return;

            if (!isNumber(portIndex)) return;

            const componentInstance = componentInstances.find(
                (componentInstance) => componentInstance.id === componentInstanceId,
            );
            const port = componentInstance?.configuration.ports[portIndex];

            if (port?.controlMethod === 'power-voltage' && port?.voltageSeries && port?.powerSeries) {
                const { voltageSeries, powerSeries } = port;

                const { value: voltageSeriesValues, unit: voltageSeriesUnit } = voltageSeries;

                const isAscending = voltageSeriesValues.every(
                    (voltage, index) => index === 0 || voltage >= voltageSeriesValues[index - 1],
                );

                if (!isAscending) {
                    throw new Error(
                        `Voltage series must be in ascending order for ${componentInstanceId} port ${portIndex}: ${voltageSeriesValues}`,
                    );
                }

                const { value: powerSeriesValues, unit: powerSeriesUnit } = powerSeries;

                const startPoint = {
                    voltage: {
                        value: 0,
                        unit: 'V',
                    },
                    power: {
                        value: powerConverter.convert({
                            value: powerSeriesValues[0],
                            from: powerSeriesUnit,
                            to: powerConverter.baseUnit,
                        }).value,
                        unit: powerConverter.baseUnit,
                    },
                };

                const endPoint = {
                    voltage: {
                        value: 1e99,
                        unit: 'V',
                    },
                    power: {
                        value: powerConverter.convert({
                            value: powerSeriesValues[powerSeriesValues.length - 1],
                            from: powerSeriesUnit,
                            to: powerConverter.baseUnit,
                        }).value,
                        unit: powerConverter.baseUnit,
                    },
                };

                const pointsInTheMiddle = voltageSeriesValues.map(
                    (voltage, index) =>
                        ({
                            voltage: {
                                value: voltageConverter.toBase({ value: voltage, from: voltageSeriesUnit }).value,
                                unit: 'V',
                            },
                            power: {
                                value: powerConverter.toBase({ value: powerSeriesValues[index], from: powerSeriesUnit })
                                    .value,
                                unit: 'W',
                            },
                        }) as { voltage: { value: number; unit: 'V' }; power: { value: number; unit: 'W' } },
                );

                droopCurves[componentInstanceId] = [startPoint, ...pointsInTheMiddle, endPoint];
            }
        };

        connections.forEach((connection) => {
            pushPointsToCurve(connection.from);
            pushPointsToCurve(connection.to);
        });

        return droopCurves;
    },

    getAggregateDroopCurve: (zone: Zone, enabledComponentInstances: DiagramComponentInstance['id'][]): DataPoint[] => {
        const curves = ZoneHelpers.getDroopCurves(zone);

        const voltageSeries: number[] = [];
        const curveFunctions: ((x: number) => number)[] = [];

        Object.entries(curves).forEach(([id, points]) => {
            if (!enabledComponentInstances.includes(id)) return;

            const voltages = points.map(
                (point) =>
                    voltageConverter.toBase({
                        value: point.voltage.value,
                        from: point.voltage.unit as (typeof voltageConverter)['units'][number],
                    }).value,
            );

            voltageSeries.push(...voltages);

            const powers = points.map(
                (point) =>
                    powerConverter.toBase({
                        value: point.power.value,
                        from: point.power.unit as (typeof powerConverter)['units'][number],
                    }).value,
            );

            const sortedVoltageSeries = voltages.sort((a, b) => a - b);
            const sortedPowerSeries = powers.sort((a, b) => a - b);

            const curve = (x: number) => {
                if (sortedVoltageSeries[0] < x && x < sortedVoltageSeries[sortedVoltageSeries.length - 1]) {
                    const k = sortedVoltageSeries.findIndex((voltage) => x < voltage);

                    const x1 = sortedVoltageSeries[k - 1];
                    const x2 = sortedVoltageSeries[k];
                    const y1 = sortedPowerSeries[k - 1];
                    const y2 = sortedPowerSeries[k];

                    const result = y1 + ((y2 - y1) / (x2 - x1)) * (x - x1);

                    return result;
                } else if (x <= sortedVoltageSeries[0]) {
                    return sortedPowerSeries[0];
                } else if (x >= sortedVoltageSeries[sortedVoltageSeries.length - 1]) {
                    return sortedPowerSeries[sortedPowerSeries.length - 1];
                }

                return 0;
            };

            curveFunctions.push(curve);
        });

        const uniqueVoltageSeries = unique(voltageSeries);

        const aggregateCurve = uniqueVoltageSeries.map((voltage) => {
            const power = curveFunctions.reduce((acc, y) => acc + y(voltage), 0);

            return {
                voltage: { value: voltage, unit: 'V' },
                power: { value: power, unit: 'W' },
            };
        });

        return aggregateCurve;
    },

    getZoneForConnection: (zones: Zone[], connectionId: string): Zone | null => {
        return (
            zones.find((zone) => {
                let connections: Zone['connections'];

                if ('initConnections' in zone && zone.initConnections?.length) {
                    connections = zone.initConnections;
                } else {
                    connections = zone.connections;
                }

                return connections.some((connection) => connection.id === connectionId);
            }) ?? null
        );
    },

    getSuggestedZoneVoltage: (diagram: Diagram, zone: Zone): Voltage | undefined => {
        const { min, max } = getMinAndMaxVoltageForZone(diagram, zone);

        if (min > -Infinity && max < Infinity) {
            return { unit: 'V', min, max, nom: null };
        }

        return undefined;
    },

    getDesignVoltage: (zone: Zone) => {
        const all: number[] = [];

        zone.connections.forEach((connection) => {
            const voltage = connection.requirements.voltage.value;

            if (voltage) {
                all.push(voltage);
            }
        });

        const [voltage] = all;

        return voltage && all.every((x) => x === voltage) ? voltage : null;
    },

    getGroundingType: (zone: Zone, measurementSystem: MeasurementSystem = MeasurementSystem.METRIC) => {
        const buses = zone.componentInstances.filter((componentInstance) => componentInstance.componentType === 'bus');

        const configurations = unique(
            buses.map((bus) => get<string | null>(bus, 'specifications.electrical.earthing.configuration') ?? ''),
        );

        return configurations
            .map((configuration) => FormatHelpers.formatGroundingConfiguration(configuration, measurementSystem))
            .filter(Boolean)
            .join(' / ');
    },
};

export { ZoneHelpers };
